import React from 'react';
import PropTypes from 'prop-types';
import { map, isEmpty, filter } from 'lodash';
import uuid from 'uuid/v1';

import {
  Button,
  Box,
  Typography,
  Modal,
  Input,
  Switch,
  Stack,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Bert } from 'meteor/themeteorchef:bert';
import methodPromise from '../../../modules/method-promise-wrapper';
import handleErrorInClient from '../../../modules/handle-error-in-client';

import TagInput from '../../components/TagInput';

import {
  importAnnotations,
  importAnnotationsToVideosProject
} from '../../../api/Annotations/annotation-methods-client';

import * as annotationsOps from '../../../api/Annotations/annotation-operations';
import * as templateOps from '../../../api/AnnotationTemplates/annotation-template-operations';
import * as videoOps from '../../../api/Videos/video-operations';

const ModalImportAnnotations = props => {
  const { openModal, setOpenModal, datasForImport } = props;
  const { videos, annotations } = datasForImport;

  const [distanceFilter, setDistanceFilter] = React.useState('5');
  const [unit, setUnit] = React.useState('km');
  const [checkTimeGPS, setCheckTimeGPS] = React.useState(false);
  const [existTimeGPSAnnotation, setExistTimeGPSAnnotation] = React.useState(false);
  const [templatesList, setTemplatesList] = React.useState([]);
  const [templateSelect, setTemplateSelect] = React.useState('none');
  const [tags, setTags] = React.useState([]);
  const [dataToImport, setDataToImport] = React.useState([]);

  const checksDatasAnnotations = () => {
    let existingTimeGPS = false;
    const annotationsTemplates = [];
    map(annotations, annotation => {
      const existTimeGPS = annotation.timeGPS;
      const template = annotationsOps.getTemplate(annotation);
      const nameTemplate = templateOps.getName(template);
      if (typeof existTimeGPS === 'number') {
        existingTimeGPS = true;
      }
      if (!annotationsTemplates.includes(nameTemplate)) annotationsTemplates.push(nameTemplate);
    });
    setTemplatesList(annotationsTemplates);
    setExistTimeGPSAnnotation(existingTimeGPS);
  };

  const handleUnit = event => {
    setUnit(event.target.value);
  };

  const handleImportAnnotations = async (
    newAnnotations,
    videoId,
    spatialSync,
    syncTimeGPS,
    annotationsProps
  ) => {
    try {
      await importAnnotations({
        variableWithObjects: newAnnotations,
        videoId,
        spatialSync,
        syncTimeGPS,
        annotationsProps
      });
      Bert.alert('Annotations created', 'success');
      setOpenModal(false);
    } catch (error) {
      handleErrorInClient(error);
      setOpenModal(false);
    }
  };
  const handleImport = () => {
    if (!isEmpty(dataToImport)) {
      map(dataToImport, async object => {
        const videoId = videoOps.getId(object.video);
        const annotationsProps = await methodPromise('annotations.annotationsFromVideo', videoId);
        const newAnnotations = { annotations: object.annotationsImport };
        handleImportAnnotations(newAnnotations, videoId, false, false, annotationsProps);
      });
    }
  };

  const handleFilters = () => {
    let distanceFilterNumber = 0;
    if (unit === 'm') distanceFilterNumber = Number(distanceFilter) / 1000;
    else distanceFilterNumber = Number(distanceFilter);
    const arrayToImport = importAnnotationsToVideosProject(
      videos,
      annotations,
      distanceFilterNumber,
      checkTimeGPS,
      templateSelect,
      tags
    );
    setDataToImport(arrayToImport);
  };

  const handleAddTag = tag => setTags(prev => [...prev, tag]);
  const handleDeleteTag = i => setTags(prev => filter(prev, (tag, index) => index !== i));

  const styleModal = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    height: 425,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4
  };

  React.useEffect(() => {
    if (!isEmpty(datasForImport)) checksDatasAnnotations();
  }, [datasForImport]);

  return (
    <Modal
      open={openModal}
      onClose={() => setOpenModal(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <>
        {isEmpty(dataToImport) ? (
          <Box sx={styleModal}>
            <Box width="100%" height="70%">
              <Typography variant="h4" fontWeight="bold">
                Filters
              </Typography>
              <Box display="flex" style={{ marginButton: '10%' }}>
                <Stack direction="row" alignItems="center">
                  <Typography>Distance max to track</Typography>
                  <Input
                    style={{ width: '20%', marginLeft: '4%', marginRight: '4%' }}
                    type="number"
                    aria-label="Demo number input"
                    placeholder="Type a number…"
                    value={distanceFilter}
                    onChange={event => setDistanceFilter(event.target.value)}
                  />
                  <FormControl>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="row-radio-buttons-group"
                      onChange={handleUnit}
                      value={unit}
                    >
                      <FormControlLabel value="km" control={<Radio />} label="km" />
                      <FormControlLabel value="m" control={<Radio />} label="metres" />
                    </RadioGroup>
                  </FormControl>
                </Stack>
              </Box>
              {existTimeGPSAnnotation && (
                <Box display="flex">
                  <Stack direction="row" alignItems="center">
                    <Typography>Time GPS</Typography>
                    <Switch
                      width="300px"
                      checked={checkTimeGPS}
                      onChange={event => setCheckTimeGPS(event.target.checked)}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  </Stack>
                </Box>
              )}

              <Box display="flex" style={{ marginTop: '2%', width: '100%' }}>
                <Stack direction="row" alignItems="center" style={{ width: '100%' }}>
                  <FormControl style={{ width: '100%' }}>
                    <InputLabel id="select-project">Templates</InputLabel>
                    <Select
                      labelId="select-project"
                      id="select-project"
                      label="Proyecto"
                      style={{ width: '50%', height: '50px' }}
                      value={templateSelect}
                      onChange={event => setTemplateSelect(event.target.value)}
                    >
                      <MenuItem value="none">-------</MenuItem>
                      {templatesList.map(item => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Box>
              <Box display="flex" style={{ marginTop: '4%', width: '100%' }}>
                <InputLabel id="select-project">Tags</InputLabel>
              </Box>
              <Box display="flex" style={{ marginButton: '20%', width: '100%' }}>
                <Stack direction="row" alignItems="center" style={{ width: '100%' }}>
                  <FormControl style={{ width: '100%' }}>
                    <TagInput
                      tags={tags}
                      suggestions={[]}
                      handleAddition={handleAddTag}
                      handleDelete={handleDeleteTag}
                      onFocus={() => {}}
                    />
                  </FormControl>
                </Stack>
              </Box>
            </Box>
            <Box display="flex" justifyContent="flex-end" width="100%" height="30%">
              <Stack direction="row">
                <Button
                  style={{ height: '30%', marginTop: '30%' }}
                  variant="outlined"
                  color="primary"
                  onClick={() => handleFilters()}
                >
                  Apply filters
                </Button>
                <Button
                  style={{ height: '30%', marginTop: '30%' }}
                  variant="outlined"
                  color="error"
                  onClick={() => setOpenModal(false)}
                >
                  Close
                </Button>
              </Stack>
            </Box>
          </Box>
        ) : (
          <Box sx={styleModal}>
            <Box width="100%" height="70%">
              <Typography variant="h6">Videos found:</Typography>
              {map(dataToImport, object => (
                <>
                  <Stack key={uuid()} direction="row">
                    <Typography
                      style={{ marginTop: '2%' }}
                      key={uuid()}
                    >{`video: ${object.video.name}`}</Typography>
                  </Stack>
                  <Stack key={uuid()} direction="row">
                    <Typography
                      key={uuid()}
                    >{`Annotations to import: ${object.annotationsImport.length}`}</Typography>
                  </Stack>
                </>
              ))}
            </Box>
            <Box display="flex" justifyContent="flex-end" width="100%" height="30%">
              <Stack direction="row">
                <Button
                  style={{ height: '30%', marginTop: '30%' }}
                  variant="outlined"
                  color="success"
                  onClick={() => handleImport()}
                >
                  Import
                </Button>
                <Button
                  style={{ height: '30%', marginTop: '30%' }}
                  variant="outlined"
                  color="primary"
                  onClick={() => setDataToImport([])}
                >
                  Back
                </Button>
                <Button
                  style={{ height: '30%', marginTop: '30%' }}
                  variant="outlined"
                  color="error"
                  onClick={() => setOpenModal(false)}
                >
                  Close
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </>
    </Modal>
  );
};

ModalImportAnnotations.defaultProps = {};

ModalImportAnnotations.propTypes = {
  openModal: PropTypes.bool.isRequired,
  setOpenModal: PropTypes.func.isRequired,
  datasForImport: PropTypes.object.isRequired
};
export default ModalImportAnnotations;
